import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { validate } from "uuid";
import type { FormField } from "../../types";
import type { Variable } from "../../../types";
import { NodeTypes } from "@/workflow_api/types/core";
import { VARIABLE_NAME_TO_API_VALUE } from "../types";

dayjs.extend(customParseFormat);

const parseTimeTo24h = (t?: string): string | null => {
  if (!t) return null;
  const s = t.trim().replace(/\s+/g, " ");
  const withAmPm = ["h:mm A", "h:mmA", "hh:mm A", "hh:mmA", "h A", "hA"];
  const withoutAmPm = ["H:mm", "HH:mm", "H.mm", "HH.mm"];

  const formats = /am|pm/i.test(s) ? withAmPm : withoutAmPm;

  const m = dayjs(s, formats, true);
  return m.isValid() ? m.format("HH:mm") : null;
};

const mapExpedientNodeId = (nodeId?: string) => {
  if (!nodeId || typeof nodeId !== "string") {
    return nodeId;
  }

  if (nodeId.includes("-expedient-")) {
    const nodeIdParts = nodeId.split("-expedient-");
    if (nodeIdParts.length >= 1 && nodeIdParts[0]) {
      return nodeIdParts[0];
    }
  }

  return nodeId;
};

const isExpedientFieldVariable = (variable: Variable) => {
  return variable?.node_id?.includes("-expedient-");
};

function resolveVariableValue(variable: Variable) {
  if (isExpedientFieldVariable(variable)) {
    return {
      type: "variable",
      variable: variable.name,
      node_id: mapExpedientNodeId(variable.node_id),
      field_id: variable.field_id,
      output_name: variable.output_name,
    };
  }

  if (variable.node_id === "global-variables") {
    return {
      type: "variable",
      variable: variable.name,
      value: variable.id,
      global_type: variable.global_type,
    };
  }

  if (variable.global_type) {
    let apiValue: string;
    if (
      variable.global_type === "LEGAL_INFO" ||
      variable.global_type === "IMPERSONATING"
    ) {
      // LEGAL_INFO and IMPERSONATING carry the exact backend value in their id; do not remap it.
      apiValue = variable.id;
    } else if (variable.global_type === "COMPANY_FIELD") {
      apiValue = VARIABLE_NAME_TO_API_VALUE[variable.name] ?? variable.id;
    } else {
      apiValue = VARIABLE_NAME_TO_API_VALUE[variable.name] ?? variable.name;
    }
    return {
      type: "variable",
      variable: variable.name,
      value: apiValue,
      node_id: variable.node_id,
      global_type: variable.global_type,
    };
  }

  if (variable?.field_id && variable?.output_name) {
    return {
      type: "variable",
      variable: variable.name,
      node_id: variable.node_id,
      field_id: variable.field_id,
      output_name: variable.output_name,
    };
  }

  if (variable?.output_name) {
    return {
      type: "variable",
      variable: variable.name,
      node_id: variable.node_id,
      output_name: variable.output_name,
    };
  }

  if (variable?.field_id) {
    return {
      type: "variable",
      variable: variable.name,
      node_id: variable.node_id,
      field_id: variable.field_id,
    };
  }

  const isValidUUID = validate(variable.id || "");

  if (!isValidUUID) {
    return {
      type: "variable",
      variable: variable.id,
      node_id: variable.node_id,
      output_name: variable.id,
    };
  }

  if (
    variable.id !== variable.node_id &&
    variable?.ref_name !== NodeTypes.VARIABLE_REPLACEMENT
  ) {
    return {
      type: "variable",
      variable: variable.name,
      node_id: variable.node_id,
      field_id: variable.id,
    };
  }

  return {
    type: "variable",
    variable: variable.name,
    node_id: variable.node_id,
  };
}

function resolveInteroperabilityValue(variable: Variable) {
  const parts = variable.id.split("_$key$_");
  const fieldPart = parts[0];
  const [responseKey, keyName] = (parts[1] || "").split("_$keyname$_");
  return {
    type: "interoperability",
    field: fieldPart,
    response_key: responseKey,
    key_name: keyName || "",
  };
}

function buildDateValue(
  includeTime: boolean | undefined,
  defaultValue: string,
  defaultTimeValue: string,
) {
  if (!defaultValue) return null;
  if (includeTime) return `${defaultValue} ${parseTimeTo24h(defaultTimeValue)}`;
  return defaultValue;
}

function formatDateWithTime(
  date: string,
  time: string,
  includeTime: boolean | undefined,
): string {
  if (includeTime) return `${date} ${parseTimeTo24h(time)}`;
  return date;
}

function buildDateRangeValue(
  includeTime: boolean | undefined,
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
) {
  if (!startDate && !endDate) return null;
  return {
    start: startDate
      ? formatDateWithTime(startDate, startTime, includeTime)
      : null,
    end: endDate ? formatDateWithTime(endDate, endTime, includeTime) : null,
  };
}

function resolveSelectDefaultValue(field: FormField) {
  if (!field.selectDefaultOptions || field.selectDefaultOptions.length === 0) {
    return "";
  }
  return { options: [...field.selectDefaultOptions] };
}

function resolveSwitchDefaultValue(value: string | undefined) {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

function resolveYearDefaultValue(yearValue: string | undefined) {
  if (!yearValue) return null;
  const parsed = Number.parseInt(yearValue, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function isYearPrecision(fieldType: string, field: FormField): boolean {
  return (
    fieldType === "year" ||
    ((fieldType === "date" || fieldType === "datetime") &&
      field.datePrecision === "year")
  );
}

export const defaultValueConverter = (fieldType: string, field: FormField) => {
  if (fieldType === "select") {
    return resolveSelectDefaultValue(field);
  }

  if (field.defaultValueType === "var" && field.defaultValueVariable) {
    return resolveVariableValue(field.defaultValueVariable);
  }

  if (field.defaultValueVariable) {
    return resolveInteroperabilityValue(field.defaultValueVariable);
  }

  if (fieldType === "switch") {
    return resolveSwitchDefaultValue(field.configDefaultValue);
  }

  if (isYearPrecision(fieldType, field)) {
    return resolveYearDefaultValue(field.configDefaultValue);
  }

  if (!fieldType.includes("date")) {
    return field.configDefaultValue;
  }

  const includeTime = field.includeTime;
  const defaultValue = field.configDefaultValue || "";
  const defaultTimeValue = field.configDefaultTimeValue || "00:00";
  const defaultStartDate = field.configDefaultStartDate || "";
  const defaultStartTime = field.configDefaultStartTime || "00:00";
  const defaultEndDate = field.configDefaultEndDate || "";
  const defaultEndTime = field.configDefaultEndTime || "00:00";

  if (fieldType === "date" || fieldType === "datetime") {
    return buildDateValue(includeTime, defaultValue, defaultTimeValue);
  }

  if (fieldType === "date_range" || fieldType === "datetime_range") {
    return buildDateRangeValue(
      includeTime,
      defaultStartDate,
      defaultStartTime,
      defaultEndDate,
      defaultEndTime,
    );
  }
};
